import { Dialog } from '@headlessui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import ErrorDisplay from './ErrorDisplay';

export default function CompanyFormModal({ isOpen, onClose, onSubmit, initialValues = null }) {
  const isEditing = !!initialValues;
  const [governingBodies, setGoverningBodies] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      // Fetch governing bodies for selection
      api.get('/api/governing-bodies')
        .then(response => setGoverningBodies(response.data))
        .catch(error => console.error('Error fetching governing bodies:', error));
    }
  }, [isOpen]);

  const validationSchema = Yup.object({
    name: Yup.string().required(t('forms.required')),
    abbreviation: Yup.string()
      .required(t('forms.required'))
      .min(2, t('companies.abbreviationMinLength'))
      .max(4, t('companies.abbreviationMaxLength'))
      .matches(/^[A-Za-z0-9]+$/, t('companies.abbreviationAlphanumeric'))
      .transform(value => value.toUpperCase()),
    email: Yup.string().email(t('forms.invalidEmail')).required(t('forms.required')),
    phone: Yup.string().nullable(),
    address: Yup.string().nullable(),
    governingBodyId: Yup.number().nullable(),
    isActive: Yup.boolean(),
    receiveMonthlyReports: Yup.boolean(),
    reportEmails: Yup.string().nullable(),
    reportScheduleDay: Yup.number().min(1).max(31).nullable(),
    reportScheduleTime: Yup.string().nullable(),
    reportTimezone: Yup.string().nullable()
  });

  const initialFormValues = initialValues || {
    name: '',
    abbreviation: '',
    email: '',
    phone: '',
    address: '',
    governingBodyId: '',
    isActive: true,
    receiveMonthlyReports: false,
    reportEmails: '',
    reportScheduleDay: 1,
    reportScheduleTime: '08:00',
    reportTimezone: 'Africa/Tripoli'
  };

  // Clear error when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      await onSubmit(values);
      setError(null);
      handleClose();
    } catch (submitError) {
      console.error('Form submission error:', submitError);
      setError(submitError);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-2xl w-full rounded bg-white p-6 my-8">
          <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
            {isEditing ? t('companies.editCompany') : t('companies.addCompany')}
          </Dialog.Title>

          {error && (
            <ErrorDisplay 
              error={error} 
              onDismiss={() => setError(null)} 
              className="mb-6"
            />
          )}
          
          <Formik
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t('companies.companyName')}
                  </label>
                  <Field
                    id="name"
                    name="name"
                    className={`mt-1 input ${errors.name && touched.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && touched.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="abbreviation" className="block text-sm font-medium text-gray-700">
                    {t('companies.abbreviation')}
                  </label>
                  <Field
                    id="abbreviation"
                    name="abbreviation"
                    placeholder={t('companies.abbreviationPlaceholder')}
                    className={`mt-1 input ${errors.abbreviation && touched.abbreviation ? 'border-red-500' : ''}`}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.abbreviation && touched.abbreviation && (
                    <p className="mt-1 text-xs text-red-500">{errors.abbreviation}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t('companies.abbreviationHelp')}
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('common.email')}
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    className={`mt-1 input ${errors.email && touched.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && touched.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    {t('common.phone')}
                  </label>
                  <Field
                    id="phone"
                    name="phone"
                    className="mt-1 input"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    {t('common.address')}
                  </label>
                  <Field
                    id="address"
                    name="address"
                    as="textarea"
                    rows={3}
                    className="mt-1 input"
                  />
                </div>

                <div>
                  <label htmlFor="governingBodyId" className="block text-sm font-medium text-gray-700">
                    {t('companies.governingBody')}
                  </label>
                  <Field
                    as="select"
                    id="governingBodyId"
                    name="governingBodyId"
                    className="mt-1 input"
                  >
                    <option value="">{t('companies.noGoverningBody')}</option>
                    {governingBodies.map(gb => (
                      <option key={gb.id} value={gb.id}>
                        {gb.name}
                      </option>
                    ))}
                  </Field>
                  <p className="mt-1 text-xs text-gray-500">
                    {t('companies.governingBodyHelp')}
                  </p>
                </div>

                <div className="flex items-center">
                  <Field 
                    id="isActive" 
                    name="isActive" 
                    type="checkbox" 
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500" 
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    {t('companies.activeCompany')}
                  </label>
                </div>

                {/* Monthly Report Settings */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">
                    📧 {t('companies.monthlyReportSettings')}
                  </h3>
                  
                  <div className="flex items-center mb-3">
                    <Field 
                      id="receiveMonthlyReports" 
                      name="receiveMonthlyReports" 
                      type="checkbox" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500" 
                    />
                    <label htmlFor="receiveMonthlyReports" className="ml-2 block text-sm text-gray-900">
                      {t('companies.receiveMonthlyReports')}
                    </label>
                  </div>

                  <div>
                    <label htmlFor="reportEmails" className="block text-sm font-medium text-gray-700">
                      {t('companies.reportEmails')}
                    </label>
                    <Field
                      id="reportEmails"
                      name="reportEmails"
                      placeholder="email1@company.com, email2@company.com"
                      className="mt-1 input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('companies.reportEmailsHelp')}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                      <label htmlFor="reportScheduleDay" className="block text-sm font-medium text-gray-700">
                        {t('companies.scheduleDay')}
                      </label>
                      <Field
                        as="select"
                        id="reportScheduleDay"
                        name="reportScheduleDay"
                        className="mt-1 input"
                      >
                        {[...Array(31)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </Field>
                    </div>

                    <div>
                      <label htmlFor="reportScheduleTime" className="block text-sm font-medium text-gray-700">
                        {t('companies.scheduleTime')}
                      </label>
                      <Field
                        type="time"
                        id="reportScheduleTime"
                        name="reportScheduleTime"
                        className="mt-1 input"
                      />
                    </div>

                    <div>
                      <label htmlFor="reportTimezone" className="block text-sm font-medium text-gray-700">
                        {t('companies.timezone')}
                      </label>
                      <Field
                        as="select"
                        id="reportTimezone"
                        name="reportTimezone"
                        className="mt-1 input"
                      >
                        <option value="Africa/Tripoli">Libya (Tripoli)</option>
                        <option value="Africa/Cairo">Egypt (Cairo)</option>
                        <option value="Europe/London">UK (London)</option>
                        <option value="America/New_York">US (New York)</option>
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditing ? t('common.updating') : t('common.creating')}...
                      </>
                    ) : (
                      isEditing ? t('common.update') : t('common.create')
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
} 